import React, { createContext, useContext, useState, useReducer, useEffect } from 'react';
import api from '../services/api';

// Create context
const LeadDetailsContext = createContext();

const initialState = {
  leadDetails: null,
  stageFlow: null,
  stageStatusFlow: [],
  preferences: {
    saved_preferences: {
      budget: { min: 75, max: 100 },
      preferences: [],
      interests: [],
      intents: [],
      tags: [],
      amenities: []
    },
    default_preferences: [],
    default_aminities: []
  },
  stats: null,
  overview: {
    call_summary: null,
    upcoming_tasks: [],
    recent_notes: [],
    latest_activities: []
  },
  tasks: [],
  activities: [],
  calls: [],
  notes: [],
  properties: [],
  bookings: [],
  loading: {
    details: false,
    preferences: false,
    stats: false,
    overview: false,
    tasks: false,
    activities: false,
    calls: false,
    notes: false,
    properties: false,
    bookings: false
  },
  error: {
    details: null,
    stageFlow: null,
    preferences: null,
    stats: null,
    overview: null,
    tasks: null,
    activities: null,
    calls: null,
    notes: null,
    properties: null,
    bookings: null
  }
};

// Define reducer actions
const ACTIONS = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  UPDATE_DATA: 'UPDATE_DATA',
  RESET_STATE: 'RESET_STATE'
};

// Reducer function
const leadDetailsReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.FETCH_START:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.dataType]: true
        },
        error: {
          ...state.error,
          [action.dataType]: null
        }
      };
    case ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        [action.dataType]: action.payload,
        loading: {
          ...state.loading,
          [action.dataType]: false
        }
      };
    case ACTIONS.FETCH_ERROR:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.dataType]: false
        },
        error: {
          ...state.error,
          [action.dataType]: action.payload
        }
      };
    case ACTIONS.UPDATE_DATA:
      return {
        ...state,
        [action.dataType]: action.updateFunc(state[action.dataType])
      };
    case ACTIONS.RESET_STATE:
      return initialState;
    default:
      return state;
  }
};

// Provider component
export const LeadDetailsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(leadDetailsReducer, initialState);

  const [leadDetails, setLeadDetails] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [calls, setCalls] = useState([]);
  const [callsPagination, setCallsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    totalCount: 0,
    pageSize: 10
  });
  const [activitiesPagination, setActivitiesPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    totalCount: 0,
    pageSize: 10
  });
  const [tasksPagination, setTasksPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNext: false,
    totalCount: 0,
    pageSize: 10
  });
  const [notes, setNotes] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [whatsappMessageCount, setWhatsappMessageCount] = useState(0);

  const [stageFlow, setStageFlow] = useState(null);
  const [stageStatusFlow, setStageStatusFlow] = useState([]);
  const [currentLead, setCurrentLead] = useState(null);
  // Define initial state
  const [defaultFilters, setDefaultFilters] = useState({
    source: [],
    occupation: [],
    urgency: [],
    budget: { min: 75, max: 100 },
    preferences: [],
  });
  

  const [loading, setLoading] = useState({
    details: false,
    preferences: false,
    stats: false,
    overview: false,
    tasks: false,
    activities: false,
    calls: false,
    notes: false,
    properties: false,
    bookings: false
  });

  const [error, setError] = useState({
    details: null,
    stageFlow: null,
    preferences: null,
    stats: null,
    overview: null,
    tasks: null,
    activities: null,
    calls: null,
    notes: null,
    properties: null,
    bookings: null
  });

  // Add a ref to track initialization
  const initialized = React.useRef(false);
  /** Dedupes concurrent page-1 task fetches (e.g. loadAllLeadData + LeadTasks mount). */
  const tasksPage1InFlightRef = React.useRef(null);

  // Reset context state when unmounted or when needed
  const resetState = React.useCallback(() => {
    dispatch({ type: ACTIONS.RESET_STATE });
  }, []);

  // Update data in a specific slice of state
  const updateData = React.useCallback((dataType, updateFunc) => {
    dispatch({ 
      type: ACTIONS.UPDATE_DATA, 
      dataType, 
      updateFunc 
    });
  }, []);

  // Implementation of all required fetch functions
  const fetchLeadDetails = React.useCallback(async (leadId) => {
    setLoadingState('details', true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/details/`);
      setLeadDetails(response.data);
      setCurrentLead(response.data);

      setLoadingState('details', false);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead details:', error);
      setLoadingState('details', false);
      
      // Store both error message and status code
      const errorInfo = {
        message: error.message || 'Failed to fetch lead details',
        status: error.response?.status || null,
        is404: error.response?.status === 404,
        is403: error.response?.status === 403
      };
      
      setErrorState('details', errorInfo);
      throw error;
    }
  }, []);

  const fetchLeadPreference = React.useCallback(async (leadId) => {
    setLoadingState('preferences', true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/preference/`);
      // Ensure budget has default values if null
      if (response.data.saved_preferences && response.data.saved_preferences.budget) {
        if (response.data.saved_preferences.budget.min === null) {
          response.data.saved_preferences.budget.min = 75;
        }
        if (response.data.saved_preferences.budget.max === null) {
          response.data.saved_preferences.budget.max = 100;
        }
      }
      setPreferences(response.data);
      setLoadingState('preferences', false);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead preferences:', error);
      setLoadingState('preferences', false);
      setErrorState('preferences', error.message || 'Failed to fetch preferences');
      throw error;
    }
  }, []);

  const fetchLeadStats = React.useCallback(async (leadId) => {
    setLoadingState('stats', true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/stats/`);
      setStats(response.data);
      setLoadingState('stats', false);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead stats:', error);
      setLoadingState('stats', false);
      setErrorState('stats', error.message || 'Failed to fetch stats');
      throw error;
    }
  }, []);

  const fetchLeadOverview = React.useCallback(async (leadId) => {
    setLoadingState('overview', true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/overview/`);
      
      // Ensure all required fields exist even if API doesn't return them
      const safeResponse = {
        call_summary: null,
        upcoming_tasks: [],
        recent_notes: [],
        latest_activities: [],
        ...response.data
      };
      
      setOverview(safeResponse);
      setLoadingState('overview', false);
      return safeResponse;
    } catch (error) {
      console.error('Error fetching lead overview:', error);
      setLoadingState('overview', false);
      setErrorState('overview', error.message || 'Failed to fetch overview');
      throw error;
    }
  }, []);

  const fetchLeadTasks = React.useCallback(async (leadId, params = {}) => {
    const page = params.page != null ? Number(params.page) : 1;
    const isAppend = page > 1;

    const execute = async () => {
      const query = {
        ...params,
        page: page < 1 ? 1 : page,
        page_size: params.page_size ?? 10,
      };
      const response = await api.get(`/calendar/leads/${leadId}/tasks/`, { params: query });

      if (response.data.results) {
        if (isAppend) {
          setTasks((prev) => [...prev, ...response.data.results]);
        } else {
          setTasks(response.data.results);
        }
        setTasksPagination({
          currentPage: response.data.current_page || 1,
          totalPages: response.data.total_pages || 1,
          hasNext: response.data.has_next || false,
          totalCount: response.data.count ?? response.data.results.length,
          pageSize: response.data.page_size || 10
        });
      } else {
        const list = Array.isArray(response.data) ? response.data : [];
        setTasks(list);
        setTasksPagination({
          currentPage: 1,
          totalPages: 1,
          hasNext: false,
          totalCount: list.length,
          pageSize: list.length || 10
        });
      }

      return response.data;
    };

    if (!isAppend) {
      const inflight = tasksPage1InFlightRef.current;
      if (inflight?.leadId === leadId && inflight.promise) {
        return inflight.promise;
      }
      setLoadingState('tasks', true);
      const promise = execute()
        .catch((error) => {
          console.error('Error fetching lead tasks:', error);
          setErrorState('tasks', error.message || 'Failed to fetch tasks');
          throw error;
        })
        .finally(() => {
          setLoadingState('tasks', false);
          if (tasksPage1InFlightRef.current?.leadId === leadId) {
            tasksPage1InFlightRef.current = null;
          }
        });
      tasksPage1InFlightRef.current = { leadId, promise };
      return promise;
    }

    try {
      return await execute();
    } catch (error) {
      console.error('Error fetching lead tasks:', error);
      setErrorState('tasks', error.message || 'Failed to fetch tasks');
      throw error;
    }
  }, []);

  const fetchLeadActivity = async (leadId, params = {}) => {
    const page = params.page != null ? Number(params.page) : 1;
    const isAppend = page > 1;
    if (!isAppend) {
      setLoadingState('activities', true);
    }
    try {
      const query = {
        ...params,
        page: page < 1 ? 1 : page,
        page_size: params.page_size ?? 10,
      };
      const response = await api.get(`/lms/leads/${leadId}/activity/`, { params: query });

      if (response.data.results) {
        if (isAppend) {
          setActivities(prev => [...prev, ...response.data.results]);
        } else {
          setActivities(response.data.results);
        }
        setActivitiesPagination({
          currentPage: response.data.current_page || 1,
          totalPages: response.data.total_pages || 1,
          hasNext: response.data.has_next || false,
          totalCount: response.data.count ?? response.data.results.length,
          pageSize: response.data.page_size || 10
        });
      } else {
        const list = Array.isArray(response.data) ? response.data : [];
        setActivities(list);
        setActivitiesPagination({
          currentPage: 1,
          totalPages: 1,
          hasNext: false,
          totalCount: list.length,
          pageSize: list.length || 10
        });
      }

      setLoadingState('activities', false);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead activities:', error);
      setLoadingState('activities', false);
      setErrorState('activities', error.message || 'Failed to fetch activities');
      throw error;
    }
  };

  const fetchLeadCalls = async (leadId, params = {}) => {
    setLoadingState('calls', true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/calls/`, { params });
      
      // Handle paginated response
      if (response.data.results) {
        // If appending to existing calls (for pagination)
        if (params.page > 1) {
          setCalls(prevCalls => [...prevCalls, ...response.data.results]);
        } else {
          setCalls(response.data.results);
        }
        
        // Update pagination metadata
        setCallsPagination({
          currentPage: response.data.current_page || 1,
          totalPages: response.data.total_pages || 1,
          hasNext: response.data.has_next || false,
          totalCount: response.data.count || response.data.results.length,
          pageSize: response.data.page_size || 10
        });
      } else {
        // Fallback for non-paginated response (backward compatibility)
        setCalls(response.data);
        setCallsPagination({
          currentPage: 1,
          totalPages: 1,
          hasNext: false,
          totalCount: response.data.length,
          pageSize: response.data.length
        });
      }
      
      setLoadingState('calls', false);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead calls:', error);
      setLoadingState('calls', false);
      setErrorState('calls', error.message || 'Failed to fetch calls');
      throw error;
    }
  };

  const fetchLeadNotes = async (leadId, params = {}) => {
    setLoadingState('notes', true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/notes/`, { params });
      setNotes(response.data);
      setLoadingState('notes', false);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead notes:', error);
      setLoadingState('notes', false);
      setErrorState('notes', error.message || 'Failed to fetch notes');
      throw error;
    }
  };

  // Data mutation functions
  const createTask = async (leadId, taskData) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/activity/`, taskData);
      
      setTasks((current) => [response.data, ...current]);
      setTasksPagination((prev) => ({
        ...prev,
        totalCount: (prev.totalCount ?? 0) + 1,
      }));

      // Also update overview's upcoming_tasks if needed
      if (response.data.status === 'pending') {
        setOverview(current => ({
          ...current,
          upcoming_tasks: [response.data, ...current.upcoming_tasks].slice(0, 3)
        }));
      }
      
      // Refresh activities after successful task creation
      await refreshActivities(leadId);
      
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  };

  const createNote = async (leadId, noteData) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/notes/`, noteData);
      
      // Update notes state with new note
      setNotes(current => [response.data, ...current]);
      
      // Update overview's recent_notes
      setOverview(current => ({
        ...current,
        recent_notes: [response.data, ...(current.recent_notes || [])].slice(0, 2)
      }));
      
      // Also add to latest activities if it exists
      setOverview(current => {
        if (!current.latest_activities) return current;

        const activity = {
          ...response.data,
          activity_type: 'note',
          timestamp: response.data.created_at
        };

        return {
          ...current,
          latest_activities: [activity, ...current.latest_activities].slice(0, 3)
        };
      });
      
      // Refresh activities after successful note creation
      await refreshActivities(leadId);
      
      return response;
    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  };

  const logCall = async (leadId, callData) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/calls/`, callData);
      
      // Update calls state with new call
      setCalls(current => [response.data, ...current]);
      
      // Update call_summary in overview
      setOverview(current => ({
        ...current,
        call_summary: response.data
      }));
      
      // Also add to latest activities if it exists
      setOverview(current => {
        if (!current.latest_activities) return current;
        
        const activity = {
          ...response.data,
          activity_type: 'call',
          timestamp: response.data.created_at
        };
        
        return {
          ...current,
          latest_activities: [activity, ...current.latest_activities].slice(0, 3)
        };
      });
      
      // Refresh activities after successful call logging
      await refreshActivities(leadId);
      
      return response.data;
    } catch (error) {
      console.error('Error logging call:', error);
      throw error;
    }
  };

  const fetchLeadProperties = async (leadId) => {
    try {
      const response = await api.get(`/lms/leads/${leadId}/suggested-properties/`);
      
      // Update leadDetails with the fetched properties
      if (leadDetails && leadDetails.id === leadId) {
        setLeadDetails(prevLead => ({
          ...prevLead,
          properties: response.data || []
        }));
      }
      setProperties(response.data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const fetchLeadBookings = async (leadId, params = {}) => {
    setLoadingState('bookings', true);
    try {
      const response = await api.get(`/lms/leads/${leadId}/bookings/`, { params });
      setBookings(response.data);
      setLoadingState('bookings', false);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead bookings:', error);
      setErrorState('bookings', error.message || 'Failed to fetch bookings');
      throw error;
    }
  };

  const fetchLeadWhatsAppMessageCount = async (leadId) => {
    try {
      const response = await api.get('/lms/whatsapp/lead-message-count/', {
        params: { lead_id: leadId }
      });
      setWhatsappMessageCount(response.data.count ?? 0);
      return response.data.count ?? 0;
    } catch (error) {
      console.error('Error fetching lead WhatsApp message count:', error);
      setWhatsappMessageCount(0);
      return 0;
    }
  };

  const shareProperty = async (leadId, propertyData) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/property-options/`, propertyData);
      
      // Update shared properties state
      setProperties(current => [response.data, ...current]);
      
      // Refresh activities after successful property sharing
      await refreshActivities(leadId);
      
      return response.data;
    } catch (error) {
      console.error('Error sharing property:', error);
      throw error;
    }
  };

  const updateLeadStatus = async (leadId, statusData) => {
    try {
      const response = await api.patch(`/lms/leads/update-status/`, {
        lead_id: leadId,
        ...statusData
      });
      
      // Update lead details
      setLeadDetails(current => ({
        ...current,
        ...response.data
      }));
      
      // Refresh activities after successful status update
      await refreshActivities(leadId);
      
      return response.data;
    } catch (error) {
      console.error('Error updating lead status:', error);
      throw error;
    }
  };

  // Load all lead data at once, modified to prevent infinite calls on error
  const loadAllLeadData = React.useCallback(async (leadId) => {
    // Skip if already initialized or no leadId
    if (initialized.current || !leadId) {
      return false;
    }
    
    // Set initialized to true immediately to prevent retries on render
    initialized.current = true;
    
    try {
      // First, fetch lead details - this is the critical check
      // If this fails with 403, we should not make any other API calls
      try {
        await fetchLeadDetails(leadId);
      } catch (error) {
        // If details returns 403, stop here and don't make other API calls
        if (error.response?.status === 403) {
          console.error('Access denied (403) for lead details. Stopping other API calls.');
          return false;
        }
        // For other errors (404, 500, etc.), still proceed with other calls
        console.error('Error fetching lead details:', error);
      }
      
      // Only proceed with other API calls if lead details was successful (200)
      // or if it was a non-403 error (we'll still try other calls for 404, etc.)
      
      // Array to hold all other fetch promises (excluding details which we already fetched)
      const fetchPromises = [
        fetchLeadPreference(leadId).catch(err => {
          console.error('Error fetching lead preferences:', err);
          return null;
        }),
        fetchLeadStats(leadId).catch(err => {
          console.error('Error fetching lead stats:', err);
          return null;
        }),
        fetchLeadOverview(leadId).catch(err => {
          console.error('Error fetching lead overview:', err);
          return null;
        }),
        fetchLeadTasks(leadId).catch(err => {
          console.error('Error fetching lead tasks:', err);
          return null;
        }),
        fetchLeadActivity(leadId).catch(err => {
          console.error('Error fetching lead activities:', err);
          return null;
        }),
        fetchLeadCalls(leadId).catch(err => {
          console.error('Error fetching lead calls:', err);
          return null;
        }),
        fetchLeadNotes(leadId).catch(err => {
          console.error('Error fetching lead notes:', err);
          return null;
        }),
        fetchStageStatusFlow().catch(err => {
          console.error('Error fetching stage status flow:', err);
          return null;
        }),
        fetchLeadProperties(leadId).catch(err => {
          console.error('Error fetching suggested properties:', err); 
          return null;
        }),
        fetchLeadBookings(leadId).catch(err => {
          console.error('Error fetching lead bookings:', err);
          return null;
        }),
        fetchLeadWhatsAppMessageCount(leadId).catch(err => {
          console.error('Error fetching lead WhatsApp message count:', err);
          return null;
        }),
      ];
      
      // Wait for all promises to resolve, even if some fail
      const results = await Promise.all(fetchPromises);
      
      // Count successful fetches
      const successCount = results.filter(result => result !== null).length;
      
      return true;
    } catch (error) {
      // This catch block should rarely be hit since we're catching individual promises
      console.error('Unexpected error loading lead data:', error);
      
      // IMPORTANT: We don't reset initialized.current here anymore
      // This prevents infinite retry loops
      
      return false;
    }
  }, [
    fetchLeadDetails,
    fetchLeadPreference,
    fetchLeadStats,
    fetchLeadOverview,
    fetchLeadTasks,
    fetchLeadActivity,
    fetchLeadCalls,
    fetchLeadNotes,
    fetchLeadProperties,
    fetchLeadBookings
  ]);

  // Add a manual reset function that can be called if needed
  const resetInitialization = () => {
    initialized.current = false;
  };

  // Add a function to retry loading specific data
  const retryLoadData = async (leadId, dataType) => {
    if (!leadId) return false;
    
    try {
      switch (dataType) {
        case 'details':
          await fetchLeadDetails(leadId);
          break;
        case 'preferences':
          await fetchLeadPreference(leadId);
          break;
        case 'stats':
          await fetchLeadStats(leadId);
          break;
        case 'overview':
          await fetchLeadOverview(leadId);
          break;
        case 'tasks':
          await fetchLeadTasks(leadId);
          break;
        case 'activities':
          await fetchLeadActivity(leadId);
          break;
        case 'calls':
          await fetchLeadCalls(leadId);
          break;
        case 'notes':
          await fetchLeadNotes(leadId);
          break;
        case 'properties':
          await fetchLeadProperties(leadId);
          break;
        case 'bookings':
          await fetchLeadBookings(leadId);
          break;
        case 'stageFlow':
          await fetchStageStatusFlow();
          break;
        default:
          console.warn(`Unknown data type: ${dataType}`);
          return false;
      }
      return true;
    } catch (error) {
      console.error(`Error retrying load for ${dataType}:`, error);
      return false;
    }
  };

  // Update loading state helper
  const setLoadingState = (key, isLoading) => {
    setLoading(prev => ({
      ...prev,
      [key]: isLoading
    }));
  };

  // Update error state helper 
  const setErrorState = (key, errorMessage) => {
    setError(prev => ({
      ...prev,
      [key]: errorMessage
    }));
  };

  // Update the updateLeadPreferences function to handle the new structure
  const updateLeadPreferences = async (leadId, data, pref_type='preference') => {
    try {

      // Make sure we have the correct format for the API
      const payload = {
        values: data || [],
        type: pref_type
      };

      const response = await api.post(`/lms/leads/${leadId}/preference/`, payload);

      // Refresh activities after successful update
      await refreshActivities(leadId);

      // Keep sidebar / dialogs in sync with saved_preferences (PreferenseSidebar reads context.preferences)
      try {
        await fetchLeadPreference(leadId);
      } catch (e) {
        console.error('Refetch preferences after update failed:', e);
      }

      return response.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  // Add a function to add a new activity
  const addActivity = async (leadId, activityData) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/activity/`, activityData);
      
      setActivities(current => [response.data, ...current]);
      setActivitiesPagination((p) => ({
        ...p,
        totalCount: (p.totalCount ?? 0) + 1,
      }));

      // Also update overview's latest_activities
      setOverview(current => ({
        ...current,
        latest_activities: [response.data, ...current.latest_activities || []].slice(0, 3)
      }));
      
      return response.data;
    } catch (error) {
      console.error('Error adding activity:', error);
      throw error;
    }
  };

  // Add a function to refresh activities
  const refreshActivities = async (leadId) => {
    if (!leadId) return;
    
    try {
      console.log('Refreshing activities for lead:', leadId);
      await fetchLeadActivity(leadId);
      
      // Also refresh overview to get latest activities
      await fetchLeadOverview(leadId);
    } catch (error) {
      console.error('Error refreshing activities:', error);
    }
  };

  // Add these new fetch functions
  const fetchStageStatusFlow = React.useCallback(async () => {    
    try {
      const response = await api.get('/lms/lead-status-flow/');
      setStageStatusFlow(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching stage status flow:', error);
      setErrorState('stageFlow', error.message || 'Failed to fetch stage flow');
      throw error;
    }
  }, []);    

  const updateLeadNote = async (leadId, noteId, noteData) => {

    try {
      const response = await api.patch(
        `/lms/leads/${leadId}/notes/${noteId}/`, 
        noteData
      );
      
      // Update the notes state after successful update
      setNotes(current => 
        current.map(note => 
          note.id === noteId 
            ? { ...note, note: noteData.notes }
            : note
        )
      );
      
      // Refresh activities after successful note update
      await refreshActivities(leadId);
      
      return response;
    } catch (error) {
      console.error('Error updating lead note:', error);
      throw error;
    }
  };

  const deleteLeadNote = async (leadId, noteId) => {
    try {
      const response = await api.delete(`/lms/leads/${leadId}/notes/${noteId}/`);
      
      // Update the notes state after successful deletion
      setNotes(current => current.filter(note => note.id !== noteId));
      
      // Refresh activities after successful note deletion
      await refreshActivities(leadId);
      
      return response;
    } catch (error) {
      console.error('Error deleting lead note:', error);
      throw error;
    }
  };

  // Add this function to your context
  const fetchCallDetails = async (callId) => {
    try {
      const response = await api.get(`/api/calls/${callId}/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching call details:", error);
      throw error;
    }
  };




  const fetchLeadsDefaultFilters = React.useCallback(async () => {
    const response = await api.get('/lms/leads/default-filters/');
    setDefaultFilters(response.data);
    return response.data;
  }, []);

  // Update lead details
  const updateLeadDetails = async (leadId, leadData) => {
    setLoadingState('details', true);
    try {
      const response = await api.patch(`/lms/leads/${leadId}/details/`, leadData);
      
      // Update the current lead details if it matches the updated lead
      if (currentLead && currentLead.id === leadId) {
        setCurrentLead(prevLead => {
          const updatedLead = {
            ...prevLead,
            ...response.data,
            customer: response.data.customer || prevLead.customer,
            source: response.data.source || prevLead.source,
            source_verbose: response.data.source_verbose || response.data.customer?.source_verbose || prevLead.source_verbose,
          };
          
          // Update contact_info if customer data is available
          if (response.data.customer) {
            updatedLead.contact_info = {
              name: `${response.data.customer.first_name} ${response.data.customer.last_name}`.trim(),
              email: response.data.customer.email,
              mobile: response.data.customer.mobile,
              country_code: response.data.customer.country_code,
              source: response.data.customer.source,
              source_verbose: response.data.customer.source_verbose
            };
          }
          
          return updatedLead;
        });
      }
      
      // Update leadDetails if it matches the updated lead
      if (leadDetails && leadDetails.id === leadId) {
        setLeadDetails(prevLead => {
          const updatedLead = {
            ...prevLead,
            ...response.data,
            customer: response.data.customer || prevLead.customer,
            source: response.data.source || prevLead.source,
            source_verbose: response.data.source_verbose || response.data.customer?.source_verbose || prevLead.source_verbose,
          };
          
          // Update contact_info if customer data is available
          if (response.data.customer) {
            updatedLead.contact_info = {
              name: `${response.data.customer.first_name} ${response.data.customer.last_name}`.trim(),
              email: response.data.customer.email,
              mobile: response.data.customer.mobile,
              country_code: response.data.customer.country_code,
              source: response.data.customer.source,
              source_verbose: response.data.customer.source_verbose
            };
          }
          
          return updatedLead;
        });
      }
      
      setLoadingState('details', false);
      
      // Refresh activities after successful update
      await refreshActivities(leadId);
      
      return response.data;
    } catch (error) {
      console.error('Error updating lead details:', error);
      setLoadingState('details', false);
      setErrorState('details', error.message || 'Failed to update lead details');
      throw error;
    }
  };

  const updateLeadProperties = async (leadId, propertyData) => {
    try {
      // Ensure propertyData is in the correct format
      const payload = Array.isArray(propertyData) ? { property_ids: propertyData } : propertyData;
      const response = await api.post(`/lms/leads/${leadId}/suggested-properties/`, payload);
      
      // Update leadDetails with the new properties and any status/stage returned by backend
      if (leadDetails && leadDetails.id === leadId) {
        setLeadDetails(prevLead => ({
          ...prevLead,
          properties: response.data?.property_ids || prevLead.properties || [],
          ...(response.data?.status != null && { status: response.data.status }),
          ...(response.data?.stage != null && { stage: response.data.stage }),
        }));
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPropertyInterestCheckStatus = async (leadId) => {
    try {
      const response = await api.get(`/lms/leads/${leadId}/trigger-property-interest-check/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching property interest check status:', error);
      throw error;
    }
  };

  const getReEngagementStatus = async (leadId) => {
    try {
      const response = await api.get(`/lms/leads/${leadId}/trigger-re-engagement/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching re-engagement status:', error);
      throw error;
    }
  };

  const triggerPropertyInterestCheck = async (leadId) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/trigger-property-interest-check/`);
      return response.data;
    } catch (error) {
      console.error('Error triggering property interest check:', error);
      throw error;
    }
  };

  const triggerReEngagement = async (leadId) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/trigger-re-engagement/`);
      return response.data;
    } catch (error) {
      console.error('Error triggering re-engagement:', error);
      throw error;
    }
  };

  const getCallNotPickedStatus = async (leadId) => {
    try {
      const response = await api.get(`/lms/leads/${leadId}/trigger-call-not-picked/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching call not picked status:', error);
      throw error;
    }
  };

  const triggerCallNotPicked = async (leadId) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/trigger-call-not-picked/`);
      return response.data;
    } catch (error) {
      console.error('Error triggering call not picked:', error);
      throw error;
    }
  };

  const removeLeadProperties = async (leadId, propertyData) => {
    try {
      // Ensure propertyData is in the correct format
      const payload = Array.isArray(propertyData) ? { property_ids: propertyData } : propertyData;
      const response = await api.delete(`/lms/leads/${leadId}/suggested-properties/`, { data: payload });
      
      // Update leadDetails by removing the specified properties
      if (leadDetails && leadDetails.id === leadId) {
        setLeadDetails(prevLead => {
          const currentProperties = prevLead.properties || [];
          const propertiesToRemove = Array.isArray(propertyData) ? propertyData : [propertyData];
          const updatedProperties = currentProperties.filter(id => !propertiesToRemove.includes(id));
          
          return {
            ...prevLead,
            properties: updatedProperties
          };
        });
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const assignLeadChannelPartner = async (leadId, channelPartnerData) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/channel-partner/`, channelPartnerData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const reassignLeadChannelPartner = async (leadId, channelPartnerData) => {
    try {
      const response = await api.patch(`/lms/leads/${leadId}/channel-partner/`, channelPartnerData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const removeLeadChannelPartner = async (leadId) => {
    try {
      const response = await api.delete(`/lms/leads/${leadId}/channel-partner/`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const restoreLead = async (leadId, notes = 'Lead restored') => {
    try {
      const response = await api.post('/lms/leads/restore/', {
        lead_ids: [leadId],
        notes: notes
      });
      
      // If restore was successful, refresh the lead details
      if (response.status === 200 && response.data.passed > 0) {
        // Reset initialization to allow reload
        initialized.current = false;
        // Fetch updated lead details
        await fetchLeadDetails(leadId);
      }
      
      return response;
    } catch (error) {
      console.error('Error restoring lead:', error);
      throw error;
    }
  };

  const markLeadNotRecoverable = async (leadId) => {
    try {
      const response = await api.post(`/lms/leads/${leadId}/mark-not-recoverable/`);

      if (leadDetails && leadDetails.id === Number(leadId)) {
        setLeadDetails(prev => ({
          ...prev,
          is_not_recoverable: true,
        }));
      }
      if (currentLead && currentLead.id === Number(leadId)) {
        setCurrentLead(prev => ({
          ...prev,
          is_not_recoverable: true,
        }));
      }

      return response;
    } catch (error) {
      console.error('Error marking lead not recoverable:', error);
      throw error;
    }
  };


  // Context value - memoized to prevent unnecessary re-renders
  const value = React.useMemo(() => ({
    leadDetails,
    preferences,
    stats,
    overview,
    tasks,
    tasksPagination,
    activities,
    activitiesPagination,
    calls,
    callsPagination,
    notes,
    properties,
    bookings,
    whatsappMessageCount,
    loading,
    error,
    fetchLeadDetails,
    fetchLeadPreference,
    fetchLeadStats,
    fetchLeadOverview,
    fetchLeadTasks,
    fetchLeadActivity,
    fetchLeadCalls,
    fetchLeadNotes,
    fetchLeadProperties,
    updateLeadProperties,
    removeLeadProperties,
    getPropertyInterestCheckStatus,
    triggerPropertyInterestCheck,
    getReEngagementStatus,
    triggerReEngagement,
    getCallNotPickedStatus,
    triggerCallNotPicked,
    fetchLeadBookings,
    loadAllLeadData,
    createTask,
    createNote,
    logCall,
    shareProperty,
    updateLeadStatus,
    resetState,
    updateLeadPreferences,
    setPreferences,
    addActivity,
    refreshActivities,
    stageStatusFlow,
    fetchStageStatusFlow,
    updateLeadNote,
    deleteLeadNote,
    resetInitialization,
    retryLoadData,
    fetchCallDetails,
    currentLead,
    defaultFilters,
    setDefaultFilters,
    fetchLeadsDefaultFilters,
    updateLeadDetails,
    error,  
    setError,
    assignLeadChannelPartner, 
    reassignLeadChannelPartner,
    removeLeadChannelPartner,
    restoreLead,
    markLeadNotRecoverable
  }), [
    leadDetails,
    preferences,
    stats,
    overview,
    tasks,
    tasksPagination,
    activities,
    activitiesPagination,
    calls,
    callsPagination,
    notes,
    loading,
    error,
    stageStatusFlow,
    currentLead,
    defaultFilters,
    properties,
    bookings,
    whatsappMessageCount,
    assignLeadChannelPartner,
    reassignLeadChannelPartner,
    removeLeadChannelPartner
  ]);

  return (
    <LeadDetailsContext.Provider value={value}>
      {children}
    </LeadDetailsContext.Provider>
  );
};

// Custom hook to use the context
export const useLeadDetails = () => {
  const context = useContext(LeadDetailsContext);
  
  if (context === undefined) {
    throw new Error('useLeadDetails must be used within a LeadDetailsProvider');
  }
  
  return context;
};

export default LeadDetailsContext;
